// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';

export type Status =
  | 'Offered'
  | 'Offered2step'
  | 'Executed'
  | 'Pending'
  | 'Failed'
  | 'Rejected'
  | 'Accepted'
  | 'Instructed'
  | 'Settled'
  | 'Requested'
  | 'Change Requested';

interface StateCellProps {
  state: Status;
  reason?: string;
  popover?: React.ReactNode;
}

const stateColor: Record<Status, string> = {
  Executed: 'success.main',
  Pending: 'warning.main',
  Failed: 'error.main',
  Rejected: 'error.main',
  Accepted: 'success.main',
  Offered: 'warning.main',
  Offered2step: 'warning.main',
  Instructed: 'warning.main',
  Settled: 'success.main',
  Requested: 'warning.main',
  'Change Requested': 'warning.main',
};
const stateLabelMap: Partial<Record<Status, string>> = {
  Offered2step: 'Offered',
};
export const StateCell: React.FC<StateCellProps> = ({ state, reason, popover }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  if (state !== 'Failed' && state !== 'Rejected') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          color={stateColor[state]}
          variant="body2"
        >
          {stateLabelMap[state] || state}
        </Typography>
        {popover && (
          <IconButton onClick={handleClick} size="small">
            <InfoOutlined sx={{ color: stateColor[state] }} fontSize="small" />
          </IconButton>
        )}
        {popover && (
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            {popover}
          </Popover>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Typography
          sx={{ cursor: 'pointer' }}
          color="error.main"
          onClick={handleClick}
          variant="body2"
        >
          {state}
        </Typography>
        <Tooltip title="Click for more info">
          <IconButton size="small" onClick={handleClick}>
            <InfoOutlined color="error" fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {!!reason?.length && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Typography variant="body2" sx={{ p: 1 }}>
            {reason}
          </Typography>
        </Popover>
      )}
    </Box>
  );
};
